import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { StudentMyLearningQuizTabStatus } from '@edtech-ui/constants/student-my-learning-tab.enum';
import { StudentClassQuizAttempt } from '@edtech-ui/core/models/quiz-attempt/get-quiz-by-student-class-id';
import { NewStudentDashboardCard, StudentDashboardStatsResponse } from '@edtech-ui/core/models/student-courses-v2/student-dashboard-stats';
import { StudentMyLearningEnrolledCourses } from '@edtech-ui/core/models/student-courses-v2/student-enroll-my-courses';
import { StudentQuizChallenge } from '@edtech-ui/core/models/student-courses-v2/student-quiz-challanges';
import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';
import { DateService } from '@edtech-ui/core/services/dateService/date.service';
import { QuizManagementService } from '@edtech-ui/core/services/quiz-management/quiz-management.service';
import { StudentCoursesService } from '@edtech-ui/core/services/student-courses/student-courses.service';
import { QuizDialogComponent } from '@edtech-ui/dashboard/student/quiz/quiz-dialog/quiz-dialog.component';
import { ViewQuizResultDialogComponent } from '@edtech-ui/dashboard/student/quiz/view-quiz-result-dialog/view-quiz-result-dialog.component';
import { environment } from 'environments/environment';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';

@Component({
	selector: 'app-my-learning',
	standalone: true,
	imports: [MatProgressSpinnerModule, CommonModule, RouterModule, MatTooltipModule,
		HasPermissionDirective,MatIconModule
	],
	templateUrl: './my-learning.component.html',
	styleUrl: './my-learning.component.scss'
})
export class MyLearningComponent implements OnInit, OnDestroy {

	isLoading = false;
	isCourseLoading = false;
	classId = environment.classroomId;
	subscription = new Subscription();
	studentDashboardStats: NewStudentDashboardCard[] = [];
	myCourses: StudentMyLearningEnrolledCourses[] = [];
	filterCourses: StudentMyLearningEnrolledCourses[] = [];
	studentCompleteQuiz: StudentClassQuizAttempt[] = [];
	courseTabStatus = StudentMyLearningQuizTabStatus;
	selectedTab: StudentMyLearningQuizTabStatus = StudentMyLearningQuizTabStatus.IN_PROGRESS;
	statusFilter: StudentMyLearningQuizTabStatus = StudentMyLearningQuizTabStatus.IN_PROGRESS;
	page = 1;
	limit = 10;
	totalCoursePages = 0;
	dateFormat = DateService.APP_DATE_TIME_SHORT;
	PermissionForStudents = PermissionForStudents;

	selectTab(tab: StudentMyLearningQuizTabStatus) {
		if (this.selectedTab === tab) return;
		this.selectedTab = tab;
		this.statusFilter = tab;
		this.page = 1;
		this.myCourses = [];
		this.filterCourses = [];
		this.getMyEnrollCourses();

		if (this._authService.hasPermission(PermissionForStudents.QUIZ_VIEW) &&
			tab === StudentMyLearningQuizTabStatus.SUBMITTED) {
			this.getStudentQuizByClassId();
		}
	}

	constructor(
		private _studentCourseService: StudentCoursesService,
		private _toastr: ToastrService,
		private router: Router,
		private _quizService: QuizManagementService,
		private dialog: MatDialog,
		private route: ActivatedRoute,
		private _authService: AuthServiceService
	) { }

	ngOnInit(): void {

		this.route.queryParams.subscribe(params => {
			const tab = params['tab'];
			if (tab === 'quiz-completed') {
				this.selectTab(StudentMyLearningQuizTabStatus.SUBMITTED);
			}
		});
		this.getStudentDashboardStats();
		this.getMyEnrollCourses();
	}

	getStudentDashboardStats() {
		this.isLoading = true;
		this.subscription.add(
			this._studentCourseService.getStudentDashboardStats().pipe(
				finalize(() => {
					this.isLoading = false;
				})
			).subscribe({
				next: (response: StudentDashboardStatsResponse) => {
					if (response.Success) {
						this.studentDashboardStats = [
							{ title: 'Enrolled Courses', studentCount: response.Data.enrolled_courses, subtext: 'Keep going!', icon: 'bi-book-half', bgColor: '#eae8fdff', iconColor: '#2c23a4ff' },
							{ title: 'Quizzes Completed', studentCount: response.Data.quizzes_completed, subtext: 'Level up!', icon: 'bi-clipboard-check-fill', bgColor: '#FFF3E0', iconColor: '#FB8C00' },
							{ title: 'Courses Completed', studentCount: response.Data.courses_completed, subtext: 'Great job!', icon: 'bi-mortarboard-fill', bgColor: '#E8F5E9', iconColor: '#2E7D32' },
							// { title: 'Total Course Available', studentCount: response.Data.total_courses_available, subtext: 'From challenges', icon: 'bi-award', bgColor: '#F3E5F5', iconColor: '#6A1B9A' },
							{ title: 'Course In Progress', studentCount: response.Data.courses_in_progress, subtext: 'Resume learning!', icon: 'bi-play-circle-fill', bgColor: '#ffe6e6', iconColor: '#e74c3c' }
						];
					}
				},
			}));
	}

	getMyEnrollCourses(loadMore = false) {
		this.isCourseLoading = true;
		this.subscription.add(
			this._studentCourseService.getMyEnrollCourses(this.page, this.limit, this.statusFilter).pipe(
				tap(response => {
					if (response.Success) {
						this.totalCoursePages = response.Data.total_pages;
						if (loadMore) {
							this.myCourses = [...this.myCourses, ...response.Data.courses];
						} else {
							this.myCourses = response.Data.courses || [];
						}
						this.filterCourses = this.myCourses;
					} else {
						this._toastr.error(response.Message);
					}
				}),
				finalize(() => {
					this.isCourseLoading = false;
				}),
				catchError(() => {
					this._toastr.error('Something went wrong while fetching courses');
					return of(null);
				})
			).subscribe()
		)
	}

	getStudentQuizByClassId() {
		this.subscription.add(
			this._quizService.getQuizByStudentClassId(this.classId).pipe(
				tap(response => {
					if (response.Success) {
						this.studentCompleteQuiz = response.Data.completed;
					} else {
						this._toastr.error(response.Message);
					}
				}),
				catchError(() => {
					this._toastr.error('Something went wrong while fetching quizzes');
					return of(null);
				})
			).subscribe()
		);
	}

	goToCourseDescriptionPage(courseId: string) {
		this.router.navigate(['/student-dashboard/course-detail-page', this.classId, courseId])
	}

	loadMore() {
		if (this.page < this.totalCoursePages) {
			this.page++;
			this.getMyEnrollCourses(true);
		}
	}


	openTakeQuizPopup(quiz: StudentQuizChallenge) {
		this.dialog.open(QuizDialogComponent, {
			width: 'auto',
			maxWidth: 'none',
			panelClass: 'custom-dialog-panel',
			disableClose: true,
			data: { quizId: quiz.quiz_id }
		}).afterClosed().subscribe(result => {

			if (result?.started) {
				this.router.navigate(['quiz', result.quizId],
					{ relativeTo: this.route.parent, queryParams: { attemptId: result.attemptId }, state: { isResume: false } });
			}
		});
	}

	goToQuizDetailPage(quizId: string, attemptId: string | null) {
		this.router.navigate(['quiz', quizId], { relativeTo: this.route.parent, queryParams: { attemptId }, state: { isResume: true } });
	}

	viewQuizResult(quiz: StudentClassQuizAttempt) {
		if (!quiz.attempt_id) {
			this._toastr.error('Attempt ID is missing for this quiz.');
			return;
		}

		this.dialog.open(ViewQuizResultDialogComponent, {
			width: 'auto',
			maxWidth: 'none',
			panelClass: 'custom-dialog-panel',
			disableClose: true,
			data: {
				quizId: quiz.quiz_id,
				attemptId: quiz.attempt_id,
				status: quiz.status,
				score: quiz.score,
				classId: this.classId,
				courseId: quiz.course_id
			}
		});
	}

	goBack(){
		this.router.navigate(['/student-dashboard/pro-course'])
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}
}